import React, {
  FC,
  useState,
  memo,
  useCallback,
  MouseEvent,
  useMemo,
} from 'react';
import clsx from 'clsx';
import { COLOR } from 'const/color';
import { ButtonComponent } from 'ReusableComponents/Button/ButtonComponent';
import { CheckboxComponent } from 'ReusableComponents/Checkbox/CheckboxComponent';
import { useDeepCompareEffect } from 'use-deep-compare';
import Swal from 'sweetalert2';
import { updatePackagesNumber } from '../../fetch/fetchHubSortListEntries';

interface ColorMapping {
  [key: string]: string;
}

const statusColorMapping: ColorMapping = {
  FINISHED: 'green',
  SCANNING: 'yellow',
  UNTOUCHED: 'red',
  OVERSCANNED: 'purple',
};

enum SortListOrderStatus {
  FINISHED,
  SCANNING,
  UNTOUCHED,
  OVERSCANNED,
}

export type ISortListOrder = {
  status: SortListOrderStatus;
  id: number;
  parcels: number;
  retailer: number;
  scannedParcels: number;
  totalParcels: number;
  partnerScansConfirmed: boolean;
  sender?: string;
};

interface SortListProps {
  orderData: ISortListOrder;
  removalChecked: boolean;
  toggleRemoval: (id: number) => void;
  index: number;
  bundleId: number;
  updateHubSortListEntries: (bundleIds: string[]) => void;
}

const SortListOrder: FC<SortListProps> = memo(
  ({
    orderData,
    removalChecked,
    toggleRemoval,
    index,
    bundleId,
    updateHubSortListEntries,
  }) => {
    const {
      status,
      id,
      scannedParcels,
      totalParcels,
      partnerScansConfirmed,
      parcels,
      retailer,
      sender,
    } = orderData;
    const [parcelCount, setParcelCount] = useState<number>(parcels);
    const [parcelCountColor, setParcelCountColor] =
      useState<string>('#00000020');

    const toggleRemovalCallback = useCallback(
      () => toggleRemoval(id),
      [id, toggleRemoval]
    );
    useDeepCompareEffect(() => {
      const color =
        parcelCount === parcels
          ? '#00000020'
          : parcelCount === scannedParcels
          ? COLOR.PROGRESS_GREEN
          : parcelCount < scannedParcels && parcelCount > 0
          ? COLOR.PROGRESS_YELLOW
          : parcelCount === 0
          ? COLOR.PROGRESS_RED
          : COLOR.PROGRESS_PURPLE;
      if (color !== parcelCountColor) {
        setParcelCountColor(color);
      }
    }, [parcelCount, parcels, scannedParcels]);
    const changeParcelCount = useCallback(
      (event: MouseEvent<HTMLButtonElement>) =>
        setParcelCount(currentParcelCount => {
          return event.currentTarget.dataset.action === 'increase'
            ? currentParcelCount + 1
            : currentParcelCount - 1;
        }),
      []
    );

    const rowColor = index % 2 ? null : 'gray-background';
    const rowClass = clsx('orderRow', rowColor);
    const retailerCell = clsx(
      rowClass,
      `sortListEntry__color-${partnerScansConfirmed ? 'green' : 'red'}`
    );
    const parcelUpdateCell = clsx(rowClass, 'parcelUpdate');
    const customColor = useMemo(
      () => ({ background: parcelCountColor, color: 'gray' }),
      [parcelCountColor]
    );
    const updatePackagesNumberInOrder = useCallback(async () => {
      Swal.fire({
        title: 'Danger zone!',
        text: `Are you sure you want to update orderId ${id} to have ${parcelCount} parcels?.`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Update Parcels',
        confirmButtonColor: '#0eb227',
        cancelButtonColor: '#ccc',
      })
        .then(async result => {
          if (result.value) {
            const booleanReturn = await updatePackagesNumber(
              id.toString(),
              parcelCount
            );
            if (booleanReturn) {
              updateHubSortListEntries([bundleId.toString()]);
            }
          } else {
            Swal.fire('The bundle is safe!');
          }
        })
        .catch(error => console.error(error.message));
    }, [bundleId, id, parcelCount, updateHubSortListEntries]);
    return (
      <>
        <div
          className={`sortListEntry__bgcolor-${statusColorMapping[status]}`}
        />
        <div className={rowClass}>{id}</div>
        <div className={rowClass}>{parcels}</div>
        <div className={retailerCell}>{retailer}</div>
        <div className={rowClass}>
          {scannedParcels}/{totalParcels}
        </div>
        <div className={rowClass}>{sender}</div>
        <div className={parcelUpdateCell}>
          <ButtonComponent
            className="updateCountButton"
            data-action="decrease"
            onClick={changeParcelCount}
            disabled={parcelCount < 1}
          >
            -
          </ButtonComponent>
          <ButtonComponent
            className="confirmCountUpdateButton"
            customColor={customColor}
            onClick={updatePackagesNumberInOrder}
          >
            {parcelCount === parcels ? `${parcelCount}` : `Set ${parcelCount}`}
          </ButtonComponent>
          <ButtonComponent
            className="updateCountButton"
            data-action="increase"
            onClick={changeParcelCount}
          >
            +
          </ButtonComponent>
        </div>
        <div className={rowClass}>
          <CheckboxComponent
            checked={removalChecked}
            value={id}
            handleChange={toggleRemovalCallback}
            checkBoxClassName="removeCheckbox"
            name=""
          />
        </div>
      </>
    );
  }
);

SortListOrder.displayName = 'SortListOrder';
SortListOrder.whyDidYouRender = true;
export default SortListOrder;
